import { gql } from '@urql/core';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useImmer } from 'use-immer';
import { useAppState } from '../../../client/providers/AppProvider';

import { CollegeSearchFilterComponentFragment, Espin_Game_Definition_Enum } from '../../../generated/spin-graphql';
import { CheckboxWithControl } from '../../input/Checkbox';
import { NumberSlider } from '../../input/NumberSlider';
import { ToggleButton } from '../../input/ToggleButton';

export type CollegeSearchFilterState = {
    gameFilters: Espin_Game_Definition_Enum[];
    useMaxDistance: boolean;
    scholarships: boolean;
    maxDistance: number;
    useInternationalSearch: boolean;
};

interface Props {
    gameData: CollegeSearchFilterComponentFragment;
    defaults: CollegeSearchFilterState;
    onChange: (s: CollegeSearchFilterState) => void;
}

gql`
    fragment CollegeSearchFilterComponent on query_root {
        espin_game_definition {
            display_name
            game_name
        }
    }
`;

export const CollegeSearchFilterComponent: React.FC<Props> = ({ defaults, gameData, onChange }) => {
    const { user } = useAppState();
    const [selectedFilters, setSelectedFilters] = useImmer<Espin_Game_Definition_Enum[]>(defaults.gameFilters);

    const { watch, control } = useForm<{
        useScholarshipsAvailable: boolean;
        maxDistance: string;
        useDistance: boolean;
        useInternationalSearch: boolean;
    }>({
        defaultValues: {
            useScholarshipsAvailable: defaults.scholarships,
            maxDistance: defaults.maxDistance.toString(),
            useDistance: defaults.useMaxDistance,
            useInternationalSearch: defaults.useInternationalSearch,
        },
    });

    const currentFormDetails = watch();

    useEffect(() => {
        onChange({
            gameFilters: selectedFilters,
            maxDistance: parseInt(currentFormDetails.maxDistance, 10),
            useMaxDistance: currentFormDetails.useDistance,
            scholarships: currentFormDetails.useScholarshipsAvailable,
            useInternationalSearch: currentFormDetails.useInternationalSearch,
        });
    }, [currentFormDetails, onChange, selectedFilters]);

    const filterElements = useMemo(() => {
        const elements: JSX.Element[] = [];
        if (!!gameData) {
            for (const gameDefinition of gameData.espin_game_definition) {
                elements.push(
                    <ToggleButton
                        key={`toggle-button-${gameDefinition.game_name}`}
                        label={gameDefinition.display_name}
                        data={gameDefinition.game_name}
                        isToggled={selectedFilters.includes(gameDefinition.game_name as Espin_Game_Definition_Enum)}
                        onClick={(data: Espin_Game_Definition_Enum) => {
                            // modify our draft
                            setSelectedFilters((draft) => {
                                if (!draft.includes(data)) {
                                    draft.push(data);
                                } else {
                                    draft.splice(draft.indexOf(data), 1);
                                }
                            });
                        }}
                    />,
                );
            }
        }

        return elements;
    }, [gameData, selectedFilters, setSelectedFilters]);

    return (
        <>
            {!!user && (
                <div className="filter-by-distance">
                    <CheckboxWithControl label={'Filter By Distance'} name="useDistance" control={control} />
                    <NumberSlider
                        htmlFor="maxDistance"
                        label="Maximum Distance (Miles)"
                        control={control}
                        required={false}
                        value={watch('maxDistance')}
                        min={10}
                        max={5000}
                        disabled={!watch('useDistance')}
                        step={10}
                        name={'maxDistance'}
                    />
                </div>
            )}
            <div className="international-scholarship-container">
                <CheckboxWithControl
                    label={'Filter By Scholarships Available'}
                    name="useScholarshipsAvailable"
                    control={control}
                />
                <CheckboxWithControl
                    label={'Search for Colleges outside of the United States'}
                    name="useInternationalSearch"
                    control={control}
                />
            </div>
            <div className="game-filters">
                <p className="label">Filter By Game</p>
                {filterElements}
            </div>
        </>
    );
};
